import {
  createPaymentMethodSetupIntent,
  removePaymentMethod,
  savePaymentMethod,
  setActivePaymentMethod,
  requestPaymentMethodEmail,
} from '@/services/paymentMethod'
import { defineStore } from 'pinia'
import {
  getCustomer,
  saveCustomerDetails,
  saveCustomerAddress,
  type AddressField,
  type Customer,
  type PaymentMethod,
} from '@/services/subscriptionCustomer'
import { AsyncOperationState } from './lib/AsyncOperationState'

import Bugsnag from '@bugsnag/js'

export const useSubscriptionCustomerStore = defineStore(
  'subscriptionCustomer',
  {
    state: () => ({
      loadState: AsyncOperationState.IDLE,
      customer: null as Customer | null,
      setupIntentLoadState: AsyncOperationState.IDLE,
      workState: AsyncOperationState.IDLE,
    }),
    actions: {
      async init(rechargeId: string) {
        if (this.loadState === AsyncOperationState.IDLE) {
          await this.load(rechargeId)
        }
      },
      async load(rechargeId: string) {
        this.loadState = AsyncOperationState.IN_PROGRESS
        try {
          this.customer = await getCustomer(rechargeId)
          this.loadState = AsyncOperationState.DONE
        } catch (e) {
          Bugsnag.notify(e as Error)
          this.loadState = AsyncOperationState.ERRORED
        }
      },
      async createPaymentMethodSetupIntent(rechargeId: string) {
        this.setupIntentLoadState = AsyncOperationState.IN_PROGRESS
        try {
          const intent = await createPaymentMethodSetupIntent(rechargeId)
          this.setupIntentLoadState = AsyncOperationState.DONE
          return intent
        } catch (e) {
          Bugsnag.notify(e as Error)
          this.setupIntentLoadState = AsyncOperationState.ERRORED
        }
      },
      async savePaymentMethod(
        rechargeId: string,
        stripePaymentMethodId: string,
      ) {
        this.workState = AsyncOperationState.IN_PROGRESS
        try {
          const intent = await savePaymentMethod(
            rechargeId,
            stripePaymentMethodId,
          )
          this.workState = AsyncOperationState.DONE
          this.load(rechargeId)
          return intent
        } catch (e) {
          Bugsnag.notify(e as Error)
          this.workState = AsyncOperationState.ERRORED
        }
      },
      async setActivePaymentMethod(
        rechargeId: string,
        paymentMethodId: string,
      ) {
        this.workState = AsyncOperationState.IN_PROGRESS
        try {
          await setActivePaymentMethod(rechargeId, paymentMethodId)
          this.workState = AsyncOperationState.DONE
          this.load(rechargeId)
        } catch (e) {
          Bugsnag.notify(e as Error)
          this.workState = AsyncOperationState.ERRORED
        }
      },
      async removePaymentMethod(rechargeId: string, paymentMethodId: number) {
        this.workState = AsyncOperationState.IN_PROGRESS
        try {
          await removePaymentMethod(rechargeId, paymentMethodId)
          this.workState = AsyncOperationState.DONE
          this.load(rechargeId)
        } catch (e) {
          Bugsnag.notify(e as Error)
          this.workState = AsyncOperationState.ERRORED
        }
      },
      async updateDetails(
        rechargeId: string,
        details: {
          first_name: string
          last_name: string
          phone: string
        },
      ) {
        this.workState = AsyncOperationState.IN_PROGRESS
        try {
          await saveCustomerDetails(rechargeId, details)
          this.workState = AsyncOperationState.DONE
          this.load(rechargeId)
          return { success: true }
        } catch (e) {
          Bugsnag.notify(e as Error)
          this.workState = AsyncOperationState.ERRORED
          return {
            success: false,
            errors: (e as { errors: Record<string, string[]> }).errors,
          }
        }
      },
      async updateAddress(
        rechargeId: string,
        addressId: number,
        address: Record<AddressField, string>,
      ) {
        this.workState = AsyncOperationState.IN_PROGRESS
        try {
          await saveCustomerAddress(rechargeId, addressId, address)
          this.workState = AsyncOperationState.DONE
          this.load(rechargeId)
          return { success: true }
        } catch (e) {
          Bugsnag.notify(e as Error)
          this.workState = AsyncOperationState.ERRORED
          return {
            success: false,
            errors: (e as { errors: Record<string, string[]> }).errors,
          }
        }
      },
      async requestPaymentMethodEmail(
        rechargeId: string,
        paymentMethodId: number,
      ) {
        this.workState = AsyncOperationState.IN_PROGRESS
        try {
          await requestPaymentMethodEmail(rechargeId, paymentMethodId)
          this.workState = AsyncOperationState.DONE
          this.load(rechargeId)
        } catch (e) {
          Bugsnag.notify(e as Error)
          this.workState = AsyncOperationState.ERRORED
        }
      },
    },
    getters: {
      cardLastFourDigits(): string | null {
        if (!this.customer) return null
        const defaultMethod = this.customer.include.payment_methods.find(
          (method) => method.default,
        )
        if (!defaultMethod) return null
        return defaultMethod.payment_details.last4
      },
      phoneNumber(): string | null {
        if (!this.customer) return null
        return this.customer.include.addresses[0]?.phone
      },
      paymentMethods(): PaymentMethod[] {
        if (!this.customer) return []
        return this.customer.include.payment_methods.filter(
          (method) => !!method.id,
        )
      },
    },
  },
)
